import React, { useState } from 'react';
import {
  Drawer,
  Button,
  Space,
  Switch,
  List,
  Typography,
  Divider,
  message,
} from 'antd';
import {
  SettingOutlined,
  DragOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ColumnConfig } from '../../hooks/useTableColumns';
import './styles.css';

const { Text } = Typography;

interface SortableItemProps {
  id: string;
  column: ColumnConfig;
  onVisibilityChange: (key: string, visible: boolean) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({
  id,
  column,
  onVisibilityChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !column.draggable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="column-settings-item"
    >
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space>
          {column.draggable && (
            <DragOutlined
              {...attributes}
              {...listeners}
              style={{ cursor: 'move', color: '#999' }}
            />
          )}
          <Text>{column.title}</Text>
          {column.fixed && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              (固定)
            </Text>
          )}
        </Space>
        <Switch
          checked={column.visible}
          onChange={(checked) => onVisibilityChange(column.key, checked)}
          checkedChildren={<EyeOutlined />}
          unCheckedChildren={<EyeInvisibleOutlined />}
        />
      </Space>
    </div>
  );
};

interface TableColumnSettingsProps {
  columns: ColumnConfig[];
  onVisibilityChange: (key: string, visible: boolean) => void;
  onOrderChange: (newOrder: string[]) => void;
  onReset: () => void;
}

const TableColumnSettings: React.FC<TableColumnSettingsProps> = ({
  columns,
  onVisibilityChange,
  onOrderChange,
  onReset,
}) => {
  const [visible, setVisible] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = columns.findIndex((col) => col.key === active.id);
      const newIndex = columns.findIndex((col) => col.key === over.id);

      const newColumns = arrayMove(columns, oldIndex, newIndex);
      const newOrder = newColumns.map((col) => col.key);
      onOrderChange(newOrder);
    }
  };

  const handleReset = () => {
    onReset();
    message.success('已恢复默认设置');
  };

  return (
    <>
      <Button
        icon={<SettingOutlined />}
        onClick={() => setVisible(true)}
      >
        列设置
      </Button>

      <Drawer
        title="列设置"
        placement="right"
        width={400}
        open={visible}
        onClose={() => setVisible(false)}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            size="small"
          >
            恢复默认
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text type="secondary">拖动调整列顺序，开关控制列显示</Text>
          </div>

          <Divider style={{ margin: 0 }} />

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={columns.map((col) => col.key)}
              strategy={verticalListSortingStrategy}
            >
              <List
                dataSource={columns}
                renderItem={(column) => (
                  <SortableItem
                    key={column.key}
                    id={column.key}
                    column={column}
                    onVisibilityChange={onVisibilityChange}
                  />
                )}
              />
            </SortableContext>
          </DndContext>
        </Space>
      </Drawer>
    </>
  );
};

export default TableColumnSettings;
